import { useSelector } from "react-redux";

import DatasetItem from "@/components/dataset/datasetList/DatasetItem";
import { Accordion } from "@/components/ui/accordion";
import { RootState } from "@/store";

const noop = () => {};

/**
 * Data Selection for the read-only Analysis Config panel.
 *
 * Reads `state.dataset.history` rather than `state.dataset.datasets`: running an
 * analysis clears the live collections and refills them with the *result*
 * datasets (see `clearDataset` + `setDatasetHistory`), so the input datasets the
 * user configured only survive in `history`.
 *
 * Renders `DatasetItem` directly instead of going through `DatasetList` — the
 * same approach `catchmentDataSection/catchmentDatasetList` uses for its own
 * data source. Going through `DatasetList` would bind this list to the live
 * collections and register a second drag-and-drop context for ids the Analysis
 * Result panel is already rendering.
 */
const AnalysisConfigDatasetList = () => {
  const history = useSelector((state: RootState) => state.dataset.history);

  const orderedDatasets = history.visualizationOrder
    .map((id) => history.datasets[id])
    .filter(Boolean);

  if (orderedDatasets.length === 0) return null;

  return (
    <Accordion type="single" collapsible className="w-full">
      <ul
        className="flex flex-col gap-2 mt-3"
        data-testid="analysis-config-view-only-dataset-list"
      >
        {orderedDatasets.map((item) => (
          <DatasetItem
            key={item.data.id}
            data={item}
            isReadOnly
            isFocused={false}
            showLabel={item?.style?.labelConfig?.show_label}
            totalRows={history.rawDatasets?.[item.data.id]?.rows.length ?? 0}
            onMouseEnter={noop}
            onMouseLeave={noop}
            onRemoveDataset={noop}
            onToggleDatasetVisibility={noop}
            onOpenDatasetTabularModal={noop}
          />
        ))}
      </ul>
    </Accordion>
  );
};

export default AnalysisConfigDatasetList;
